'use client';

import {
  extractUtmFromUrl,
  getUtmCookieName,
  hasUtmParams,
  type UtmParams,
} from '@/shared/utils/utmTracking';
import { useEffect } from 'react';
import useCookie from 'react-use-cookie';

/**
 * Компонент для отслеживания и сохранения UTM-меток в cookies
 */
export function UtmTracker() {
  const [_, setUtmCookie] = useCookie(getUtmCookieName(), JSON.stringify({}));

  useEffect(() => {
    // Извлекаем UTM параметры из текущего URL
    const utmParams = extractUtmFromUrl();

    // Если есть UTM параметры в URL, сохраняем их в cookies
    if (hasUtmParams(utmParams)) {
      try {
        const cookieValue = JSON.stringify(utmParams);
        setUtmCookie(cookieValue, {
          days: 1, // 1 день
          SameSite: 'Lax',
        });
      } catch (error) {
        console.error('Error saving UTM params to cookie:', error);
      }
    }
  }, []); // Выполняется только при монтировании

  // Отслеживаем изменения URL (например, при навигации)
  useEffect(() => {
    const handleLocationChange = () => {
      const utmParams = extractUtmFromUrl();

      if (hasUtmParams(utmParams)) {
        try {
          const cookieValue = JSON.stringify(utmParams);
          setUtmCookie(cookieValue, {
            days: 1,
            SameSite: 'Lax',
          });
        } catch (error) {
          console.error('Error updating UTM params in cookie:', error);
        }
      }
    };

    // Слушаем события popstate (навигация назад/вперед)
    window.addEventListener('popstate', handleLocationChange);

    // Используем MutationObserver для отслеживания изменений URL через pushState/replaceState
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function (...args) {
      originalPushState.apply(history, args);
      setTimeout(handleLocationChange, 0);
    };

    history.replaceState = function (...args) {
      originalReplaceState.apply(history, args);
      setTimeout(handleLocationChange, 0);
    };

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [setUtmCookie]);

  return null; // Компонент не рендерит ничего
}

/**
 * Хук для получения UTM параметров из cookies
 */
export function useUtmParams(): UtmParams {
  const [utmCookie] = useCookie(getUtmCookieName(), JSON.stringify({}));

  try {
    const params = JSON.parse(utmCookie) as UtmParams;
    return params;
  } catch (error) {
    console.error('Error parsing UTM params from cookie:', error);
    return {};
  }
}
